<i18n src="./translations"></i18n>

<template>
  <div :class="classes" :data-preload="[$options.name]">
    <div class="wrapper">
      <div
        class="input-wrapper"
        :class="{
          'input-wrapper--focused': selected,
          'input-error': isMaxQuantityReached,
        }"
      >
        <Button
          class="quantity-btn remove"
          icon="remove"
          :disabled="disabled || inputValue <= 1"
          :on-click="decrement"
        />
        <input
          v-model.number="inputValue"
          class="input"
          :class="{ 'input--focused': selected }"
          pattern="\d*"
          maxLength="3"
          type="number"
          @input="validateInput"
          @change="onInputChange()"
          @focus="onFocus"
          @blur="onBlur"
        />
        <Button
          class="quantity-btn add"
          icon="add"
          :disabled="disabled || inputValue >= maxQuantity"
          :on-click="increment"
        />
      </div>
    </div>

    <div
      v-if="isMaxQuantityReached && !isCartAndCheckout"
      class="max-quantity-error"
    >
      {{ $t('quantity-selector-26.max-quantity-error') }}
    </div>
  </div>
</template>

<script>
import globalMixin from 'Libs/mixins/globalMixin';
import { mapState, mapActions } from 'vuex';
import { axios } from 'Services/AjaxService';
import Button from 'Src/components/01-atoms/button/Button.vue';

export default {
  name: 'QuantitySelector26',
  components: {
    Button,
  },
  mixins: [globalMixin],
  props: {
    product: {
      type: Object,
      default: () => ({
        productId: '',
        sku: '',
        availability: '',
      }),
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    hasLabel: {
      type: Boolean,
      default: true,
    },
    hasVerticalDivider: {
      type: Boolean,
      default: true,
    },
    value: {
      type: Number,
      default: 0,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    choices: {
      type: Array,
      default: () => [
        {
          label: '1',
          value: '1',
        },
        {
          label: '2',
          value: '2',
        },
        {
          label: '3',
          value: '3',
        },
        {
          label: '4',
          value: '4',
        },
        {
          label: '5',
          value: '5',
        },
        {
          label: '6',
          value: '6',
        },
        {
          label: '7',
          value: '7',
        },
        {
          label: '8',
          value: '8',
        },
        {
          label: '9',
          value: '9',
        },
      ],
    },
    threshold: {
      type: Number,
      default: 10,
    },
    isMinicart: {
      type: Boolean,
      default: false,
    },
    isCartAndCheckout: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    inputValue: 1,
    selected: false,
    maxQuantity: 999,
    isMaxQuantityReached: false,
    prevAvailability: '', // To monitor Availability changes for Minicart since it gets updated through the store
  }),
  computed: {
    ...mapState('core', {
      salesChannelId: ({ salesChannel }) => salesChannel.salesChannelId,
      viewport: ({ viewport: { range } }) => range,
    }),
    ...mapState('miniCart', {
      minicartProducts: (state) => state.data?.products || null,
    }),
    stockAvailabilityUrl() {
      return this.frontFacingPath('stock.ajax.availability');
    },
  },
  watch: {
    value(value) {
      if (this.inputValue !== value) {
        this.inputValue = value || 1;
        this.prevAvailability = this.product?.availability;
      }
    },
    minicartProducts(newMinicartProducts) {
      if (this.isMinicart && newMinicartProducts) {
        this.getQuantity();
      }
    },
  },
  mounted() {
    this.inputValue = this.value || 1;

    this.prevAvailability = this.product?.availability;
  },
  methods: {
    ...mapActions('messages', ['addMessage']),
    increment() {
      if (this.disabled) return;

      const current = parseInt(this.inputValue, 10) || 0;
      let next = current + 1;

      if (next > this.maxQuantity) {
        next = this.maxQuantity;
        if (!this.isMaxQuantityReached) {
          this.isMaxQuantityReached = true;
          this.$emit('maxQuantityReached', this.isMaxQuantityReached);
        }
      } else if (this.isMaxQuantityReached) {
        this.isMaxQuantityReached = false;
        this.$emit('maxQuantityReached', this.isMaxQuantityReached);
      }

      this.inputValue = next;
      this.onInputChange();
    },
    decrement() {
      if (this.disabled) return;

      const current = parseInt(this.inputValue, 10) || 0;
      const next = Math.max(1, current - 1);

      if (this.isMaxQuantityReached && next <= this.maxQuantity) {
        this.isMaxQuantityReached = false;
        this.$emit('maxQuantityReached', this.isMaxQuantityReached);
      }

      this.inputValue = next;
      this.onInputChange();
    },
    checkStockChanges(newAvailability, newMaxStock) {
      this.maxQuantity = newMaxStock ? newMaxStock : this.maxQuantity;

      if (this.inputValue > this.maxQuantity) {
        this.inputValue = this.maxQuantity;
        this.isMaxQuantityReached = true;
        this.$emit('maxQuantityReached', this.isMaxQuantityReached);
      }

      const isMinicartAvailabilityUpdated =
        this.isMinicart &&
        this.prevAvailability &&
        newAvailability !== this.prevAvailability;

      if (
        this.product.availability !== newAvailability ||
        isMinicartAvailabilityUpdated
      ) {
        this.product.availability = newAvailability;
        this.prevAvailability = newAvailability;

        this.addMessage({
          icon: 'info-filled',
          text: this.$t('quantity-selector-26.stock-availability-toast'),
          type: 'light-blue',
          style:
            this.viewport === 'mobile' && this.isMaxQuantityReached
              ? 'margin-bottom: 70px'
              : '',
        });
      }
    },
    async fetchQuantity() {
      try {
        const { data } = await axios.post(
          this.stockAvailabilityUrl,
          {
            products: [
              {
                sku: this.product?.sku,
                demanded_quantity: this.inputValue,
              },
            ],
          },
          {
            headers: { 'X-Sales-Channel-ID': this.salesChannelId },
          }
        );

        return data?.find((item) => item.sku === this.product.sku);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching quantity:', error);
        return {};
      }
    },
    async getQuantity() {
      this.isMaxQuantityReached = false;

      let productStock = this.isMinicart
        ? this.minicartProducts.find(
            (product) => product.sku === this.product.sku
          )
        : null;

      if (!productStock) productStock = await this.fetchQuantity();

      if (!productStock) return;

      this.checkStockChanges(
        productStock.availability || productStock.availabilityStatus,
        productStock.maxStock
      );
    },
    async onInputChange() {
      if (!this.isMinicart) await this.getQuantity();

      const parsedInput = parseInt(this.inputValue, 10);

      if (!Number.isInteger(parsedInput) || parsedInput <= 0) {
        this.inputValue = this.choices[0].value;
      }

      this.emitChanges();
    },
    validateInput(event) {
      // Strip non-numeric characters
      let cleaned = event.target.value.replace(/\D/g, '');

      // Limit to 3 characters
      if (cleaned.length > 3) {
        cleaned = cleaned.slice(0, 3);
      }

      this.inputValue = cleaned;
    },
    emitChanges() {
      this.$emit('onQuantityChange', {
        productId: this.product?.productId,
        quantity: this.inputValue,
      });
    },
    onFocus() {
      this.selected = true;
    },
    onBlur() {
      this.selected = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import 'variables/breakpoints';
@import 'mixins';

.input-wrapper {
  display: flex;

  .input {
    text-align: center;
    border-top: 1px solid var(--color-rebranding-frenchGray);
    border-bottom: 1px solid var(--color-rebranding-frenchGray);
    border-left: 0;
    border-right: 0;
    border-radius: 0;
  }

  .quantity-btn {
    border: 1px solid var(--color-rebranding-frenchGray);
    background: var(--color-rebranding-alabaster);

    &.add {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    &.remove {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
}

.quantitySelector26 {
  min-width: 72px;
  margin-bottom: var(--space-1);

  --row-height: 48px;

  .wrapper {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;

    .inputWrapper {
      width: 82px;
      margin-right: var(--space-1);
      margin-top: var(--space-1);

      .input {
        height: var(--row-height);
        color: var(--color-blackout);
      }
    }
  }

  .max-quantity-error {
    color: var(--color-rebranding-semantic-red);
    margin-top: var(--space-1);
    line-height: var(--space-2--half);
  }

  &--error {
    .error,
    .label {
      color: var(--color-red);
    }
  }

  &--disabled {
    pointer-events: none;

    .inputWrapper {
      &::before {
        content: none;
      }
    }

    .input {
      color: var(--color-rolling-stone);
    }

    .icon {
      display: none;
    }
  }

  &--showInput {
    .inputWrapper {
      display: block;
    }
  }

  &--pending {
    .inputWrapper {
      .icon {
        fill: var(--color-tangerine);
      }
    }
  }

  &--centeredIcon {
    .icon {
      right: 15px;

      &.reloadSvg {
        right: 7px;
      }
    }
  }

  &--centeredIcon {
    .icon {
      right: 15px;

      &.reloadSvg {
        right: 7px;
      }
    }
  }

  .icon {
    right: 20px;
  }

  &--slim {
    .inputWrapper {
      &::before {
        content: '';
        width: 35px;
      }
    }

    .icon {
      right: 14px;
    }
  }
}

.inputWrapper {
  position: relative;
  cursor: pointer;

  &.input-error {
    .input {
      border-color: var(--color-rebranding-semantic-red) !important;
    }
  }
}

.inputWrapper {
  .icon {
    fill: var(--color-black);
  }
}

.label {
  display: block;
  position: absolute;
  top: 5px;
  left: 8px;

  font-size: var(--font-size-XS);
  color: var(--color-black);

  &--required::after {
    content: '*';
  }
}

.input {
  appearance: none;
  outline: none;
  background: var(--color-white);
  height: 40px;
  width: 100%;
  transition: all var(--time-S);

  font-weight: bold;
  font-size: var(--font-size-M);
  line-height: 16px;

  border-top: 1px solid var(--color-charcoal-gray);
  border-bottom: 1px solid var(--color-charcoal-gray);
  border-left: 0;
  border-right: 0;
  border-radius: var(--border-radius-default);

  &--focused {
    border-top: 1px solid var(--color-chathamsblue) !important;
    border-bottom: 1px solid var(--color-chathamsblue) !important;
    border-left: 0 !important;
    border-right: 0 !important;
  }
}

.input {
  appearance: none;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
}

.icon {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translate(0, -50%);

  transition: fill var(--time-S) ease-in-out;

  &.reloadSvg {
    right: 11px;
  }
}

.error,
.message {
  padding-top: var(--space-0--half);
  padding-left: var(--space-XXS);

  color: var(--color-black--primary);
  font-size: var(--font-size-S);
  line-height: 16px;
}

@media print {
  .quantitySelector26 {
    &--disabled {
      width: 60px;

      .input {
        width: 60px;
      }
    }
  }
}
</style>
